import { createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { fx } from 'src/libs'
import { gatewayBlockChanged } from './blockGateway'
import { gatewayBlocksUrl, LOOKUP_LIMIT } from './const'
import { ClientGateway, ClientGatewayResponse, SelectOption } from './types'
import { toUniqueOptions } from './utils'

export const GatewayBlocksLookupGate = createGate<{ clientId: string }>()

export const getGatewayBlocksLookupFx = fx<void, ClientGatewayResponse>({
  method: 'GET',
})

const $lookupGateways = createStore<ClientGateway[]>([]).on(
  getGatewayBlocksLookupFx.doneData,
  (_, response) => response.body.items,
)

export const $blockedCount = $lookupGateways.map(
  (gateways) => gateways.filter((gateway) => gateway.isBlocked).length,
)

export const $blockedGatewayIds = $lookupGateways.map(
  (gateways) =>
    new Set(
      gateways
        .filter((gateway) => gateway.isBlocked)
        .map((gateway) => gateway.gatewayId),
    ),
)

export const $providerOptions = $lookupGateways.map<SelectOption[]>(
  (gateways) => toUniqueOptions(gateways, 'processingProvider'),
)

export const $typeOptions = $lookupGateways.map<SelectOption[]>((gateways) =>
  toUniqueOptions(gateways, 'paymentMethodType'),
)

sample({
  clock: GatewayBlocksLookupGate.state,
  filter: ({ clientId }) => Boolean(clientId),
  fn: ({ clientId }) => ({
    url: gatewayBlocksUrl(clientId),
    query: { page: '1', limit: String(LOOKUP_LIMIT) },
  }),
  target: getGatewayBlocksLookupFx,
})

// The gate only fires on mount, so the client id has to be remembered for the
// refetch that keeps the Blocked badge and the filter options in sync.
const $lookupClientId = createStore<string>('').on(
  GatewayBlocksLookupGate.state,
  (_, state) => state?.clientId ?? '',
)

sample({
  clock: gatewayBlockChanged,
  source: $lookupClientId,
  filter: Boolean,
  fn: (clientId) => ({
    url: gatewayBlocksUrl(clientId),
    query: { page: '1', limit: String(LOOKUP_LIMIT) },
  }),
  target: getGatewayBlocksLookupFx,
})

sample({
  clock: getGatewayBlocksLookupFx.failData,
  fn: (error): NotificationPayload => ({
    type: 'error',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})
